import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Col, ControlLabel, Grid, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '../Authorization/useAuthorization';
import { TextBoxUsersMentions } from '../CustomControls/TextBoxUsersMentions';
import { Add, Edit, Enabled, Remove, Save } from '../Icons/Icons';
import { getFullNameFromUserMention } from '../Utils/Commons';
import { dateTimeFormatter } from '../Utils/Formatter';
import { RejectExternalNote } from './Admin/Modals';
import { noteEnum } from '../../variables/Enums';
import { Tooltip } from '../Utils/Tooltips';

export const DocumentNotes = (props) => {
    const [, user] = useAuthorization();
    const [modal, setModal] = useState(null);
    const { t } = useTranslation();
    const [highlightedNoteId, setHighlightedNoteId] = useState(null);

    useEffect(() => {
        if (!props.highlightNoteId || !props.notes)
            return;

        const noteId = parseInt(props.highlightNoteId, 10);

        const node = document.getElementById(`document-note-${noteId}`);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedNoteId(noteId);
            const timeout = setTimeout(() => setHighlightedNoteId(null), 4000);
            return () => clearTimeout(timeout);
        }
    }, [props.highlightNoteId, props.notes]);

    if (!props.notes)
        return null;

    const handleEdit = (noteId) => {
        if (props.onEdit)
            props.onEdit(noteId);
    }

    const handleChangeState = (noteId, status) => {
        if (!props.onStatusChange)
            return;

        if (status === noteEnum.Invalid)
            setModal(
                <RejectExternalNote
                    onClose={() => setModal(null)}
                    onConfirm={(confirmChangeState)}
                    id={noteId}
                    status={status}
                />
            );
        else
            props.onStatusChange(noteId, status);
    }
    const confirmChangeState = (id, status, response) => {
        setModal(null);
        props.onStatusChange(id, status, response);
    }

    return (
        <Grid fluid>
            {modal}
            {
                props.notes.sort(function (a, b) {
                    return new Date(b.created) - new Date(a.created);
                }).map((note, index) => (
                    <Row
                        key={`note-idx-${index}`}
                        id={`document-note-${note.id}`}
                        style={{
                            padding: '10px 0',
                            borderBottom: '1px solid #eee',
                            transition: 'background-color 0.5s ease',
                            backgroundColor: highlightedNoteId === note.id ? '#fff3cd' : 'transparent'
                        }}
                    >
                        <Col sm={3}>
                            <div>{dateTimeFormatter(note.created)}</div>
                            <div>{note.createdBy}</div>
                        </Col>
                        <Col sm={7}>
                            <div>{getFullNameFromUserMention(note.note)}</div>
                            {
                                note.status !== noteEnum.Initial &&
                                <div>
                                    <small>
                                        {t(note.status === noteEnum.Invalid ? "notes.status.invalid" : "notes.status.fixed")}
                                        {" - "}{t("notes.externalNotes.responseBy")}: {note.responseBy} ({dateTimeFormatter(note.responseDate)})
                                        {note.status === noteEnum.Invalid && note.response ? ` - ${note.response}` : ""}
                                    </small>
                                </div>
                            }
                        </Col>
                        <Col sm={2}>
                            {
                                user?.sub === note.createdById &&
                                <button className="btn-table-actions" type="button" onClick={() => handleEdit(note.id)}><Edit /></button>
                            }
                            {
                                note.status === noteEnum.Initial &&
                                <React.Fragment>
                                    <button className="btn-table-actions" type="button" onClick={() => handleChangeState(note.id, noteEnum.Fixed)}>
                                        <Tooltip tooltip={t("notes.status.fixed")} id={"tooltipdocnotestatusfixed-" + index}>
                                            <Enabled />
                                        </Tooltip>
                                    </button>
                                    <button className="btn-table-actions btn-remove" type="button" onClick={() => handleChangeState(note.id, noteEnum.Invalid)}>
                                        <Tooltip tooltip={t("notes.status.invalid")} id={"tooltipdocnotestatusinvalid-" + index}>
                                            <Remove />
                                        </Tooltip>
                                    </button>
                                </React.Fragment>
                            }
                        </Col>
                    </Row>
                ))
            }
        </Grid>
    );
}

DocumentNotes.propTypes = {
    notes: PropTypes.array.isRequired,
    documentId: PropTypes.number.isRequired,
    onEdit: PropTypes.func.isRequired,
    onStatusChange: PropTypes.func,
    highlightNoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export const DocumentNoteForm = ({ onCreate, onEdit, note, protocolId }) => {
    const { t } = useTranslation();
    const [text, setText] = useState("");

    useEffect(() => {
        if (note)
            setText(note.note);
    }, [note]);

    const handleChange = (_name, value) => {
        setText(value);
    }

    const handleCreate = () => {
        if (onCreate)
            onCreate(text);
        setText("");
    }

    const handleEdit = () => {
        if (onEdit) {
            onEdit(note.id, text);
        }
        setText("");
    }

    return (
        <Grid fluid>
            <Row>
                <Col sm={12}>
                    <ControlLabel>{t("protocols.visitTracking.documentNotes")}</ControlLabel>
                </Col>
                <Col sm={10}>
                    <TextBoxUsersMentions name="notes" value={text} onChange={handleChange} isInternalNotes={true} protocolId={protocolId} />
                </Col>
                <Col sm={2}>
                    {
                        note
                            ?
                            <button className="btn-table-actions" style={{ fontSize: '24px', fontWeight: '700', lineHeight: '1' }} type="button" onClick={handleEdit}><Save /></button>
                            :
                            <button className="btn-table-actions" style={{ fontSize: '24px', fontWeight: '700', lineHeight: '1' }} type="button" onClick={handleCreate}><Add /></button>
                    }
                </Col>
            </Row>
        </Grid>
    );
}

DocumentNoteForm.propTypes = {
    note: PropTypes.object,
    onCreate: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    protocolId: PropTypes.number.isRequired
};